/**
 * Locale handling for the public marketing pages.
 *
 * The app has seven active UI languages, all served at the same URL and chosen
 * by the `rra_lang` cookie. That works for a logged-in user picking a language,
 * but it means a search engine only ever sees one version of the site — there
 * is no distinct URL to index, so every non-default language is invisible.
 *
 * Only Turkish and English have real marketing copy: measured against the live
 * translation API, `de`, `ar`, `hi`, `ru` and `fr` return the English string for
 * ~1,085 of the 1,147 `landing.*` keys. Publishing URLs for those would mean
 * five sets of fifteen English duplicate pages, which is worse than not being
 * indexed at all. They stay cookie-only UI conveniences.
 *
 * URL shape: Turkish stays at the root (`/pricing`) so no existing URL or
 * ranking moves, and English is prefixed (`/en/pricing`).
 */

export const DEFAULT_MARKETING_LOCALE = 'tr';

/** Locales that get their own indexable URLs. */
export const MARKETING_LOCALES = ['tr', 'en'] as const;

export type MarketingLocale = (typeof MARKETING_LOCALES)[number];

/** Locales other than the default, i.e. the ones carrying a path prefix. */
export const PREFIXED_MARKETING_LOCALES = MARKETING_LOCALES.filter(
  (l) => l !== DEFAULT_MARKETING_LOCALE,
);

export const LOCALE_HEADER = 'x-marketing-locale';
/** The public path as the visitor requested it, prefix included. */
export const LOCALE_PATH_HEADER = 'x-marketing-path';

export function isMarketingLocale(value: string | null | undefined): value is MarketingLocale {
  return !!value && (MARKETING_LOCALES as readonly string[]).includes(value);
}

/**
 * Split a request path into its locale prefix and the underlying route.
 *
 * `/en/pricing` → `{ locale: 'en', path: '/pricing' }`
 * `/pricing`    → `{ locale: 'tr', path: '/pricing' }`
 * `/en`         → `{ locale: 'en', path: '/' }`
 */
export function splitLocale(pathname: string): { locale: MarketingLocale; path: string } {
  for (const locale of PREFIXED_MARKETING_LOCALES) {
    if (pathname === `/${locale}`) return { locale, path: '/' };
    if (pathname.startsWith(`/${locale}/`)) {
      return { locale, path: pathname.slice(locale.length + 1) };
    }
  }
  return { locale: DEFAULT_MARKETING_LOCALE, path: pathname };
}

/**
 * Build the public URL path for a route in a given locale.
 *
 * `localizePath('/pricing', 'en')` → `/en/pricing`
 * `localizePath('/pricing', 'tr')` → `/pricing`
 *
 * Anchors and query strings are preserved, and a path that already carries a
 * prefix is re-localised rather than double-prefixed.
 */
export function localizePath(path: string, locale: MarketingLocale): string {
  if (!path.startsWith('/')) return path;

  const hashIndex = path.search(/[?#]/);
  const bare = hashIndex === -1 ? path : path.slice(0, hashIndex);
  const suffix = hashIndex === -1 ? '' : path.slice(hashIndex);

  const { path: unprefixed } = splitLocale(bare);

  if (locale === DEFAULT_MARKETING_LOCALE) return `${unprefixed}${suffix}`;
  if (unprefixed === '/') return `/${locale}${suffix}`;
  return `/${locale}${unprefixed}${suffix}`;
}
